/**
 * My Story Workshop (lesson 11) + POW Author Studio (lesson 12).
 * A storybook panel: page tabs across the seven parts, page art where the
 * catalog names an asset, big readable text, the handwriting pad below.
 * "My book" text stays in writingLocalStore.
 */
import React, { useMemo, useState } from "react";
import WritingHandwritingPad from "../WritingHandwritingPad";
import writingLocalStore from "../writingLocalStore";
import { WRITING_L11, WRITING_L12, WRITING_L12_PARTS, WRITING_STORY_PARTS } from "../../../../data/writingCurriculum";
import { useDragEngine, useDropTarget } from "../../interaction/useDragEngine";
import { ConfettiBurst } from "../../feedback/Celebration";
import { useBreakpoint } from "../../shell/LearnShell";
import { LEARN } from "../../theme";
import {
  ActionButton,
  ArrowIcon,
  Arena,
  Board,
  Caption,
  CheckCard,
  CheckIcon,
  HighlightedText,
  PadDock,
  Panel,
  PictureTile,
  ProgressRing,
  Rail,
  RoundPill,
  SpeakerButton,
  SpeakerIcon,
  StoryBook,
  StoryLine,
  WordCard,
  WordSlot,
  darken,
  seededShuffle,
  useBurst,
  useCompleteOnce,
  useLater,
  useRoundCue,
  useShake,
  wash,
} from "./arenaKit";

const useCompact = () => {
  const bp = useBreakpoint();
  return bp === "xs" || bp === "sm";
};

const KIND_TO_PART = { who: "who", where: "wherePart", when: "whenPart", what: "whatHappens" };
const partTitle = (parts, id) => parts.find((p) => p.id === id)?.title || "";

function WritePad({ target, accent, onAccepted, onWrong, resetKey }) {
  return (
    <PadDock>
      <WritingHandwritingPad
        key={resetKey ?? target}
        target={target}
        accent={accent}
        showsGuide={false}
        mode="word"
        onAccepted={onAccepted}
        onRejected={() => onWrong?.()}
      />
    </PadDock>
  );
}

function DropWordSlot({ registerTarget, id, index, ...rest }) {
  const data = useMemo(() => ({ index }), [index]);
  const ref = useDropTarget(registerTarget, id, data);
  return <WordSlot ref={ref} {...rest} />;
}

/** Lines of the lesson-11 story, saved text first, curriculum model otherwise. */
function lesson11Lines(state) {
  const fallback = {
    who: WRITING_L11.whoGrown,
    wherePart: WRITING_L11.whereSentence,
    whenPart: WRITING_L11.whenSentence,
    order: "First Max woke up, then he found a ball, and last he shared it",
    whatHappens: WRITING_L11.whatGrown,
    problem: WRITING_L11.problem,
    ending: state.chosenEnding || WRITING_L11.endings[0],
  };
  return WRITING_STORY_PARTS.map((part) => ({ part, text: state.lines?.[part.id] || fallback[part.id] }));
}

// ── Part Words ───────────────────────────────────────────────────────────────

export const PartWordsGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const items = WRITING_L11.partWords;
  const accent = activity.accent;
  const compact = useCompact();
  const [index, setIndex] = useState(0);
  const complete = useCompleteOnce(onComplete);
  const [burst, fire] = useBurst();
  const later = useLater();
  const item = items[Math.min(index, items.length - 1)];
  const partId = KIND_TO_PART[item.kind] || item.kind;
  const doneIds = items.slice(0, index).map((i) => KIND_TO_PART[i.kind] || i.kind);
  useRoundCue(speak, `${item.word}. ${item.word}.`, index);

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={18} originY={20} reduceMotion={reduceMotion} />
      <StoryBook parts={WRITING_STORY_PARTS} activeId={partId} doneIds={doneIds} accent={accent} art={item.assetName} artAlt={item.word} compact={compact} reduceMotion={reduceMotion}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: compact ? 30 : 38, fontWeight: 700, lineHeight: 1 }}>{item.word}</span>
          <SpeakerButton accent={accent} size={44} reduceMotion={reduceMotion} ariaLabel={`Hear ${item.word}`} onClick={() => speak?.(`${item.word}. ${item.word}.`, { interrupt: true, dedupe: false })} />
          <div style={{ flex: 1 }} />
          <RoundPill done={Math.min(index + 1, items.length)} total={items.length} accent={accent} />
        </div>
        <Caption style={{ textAlign: "left" }}>A {partTitle(WRITING_STORY_PARTS, partId).toLowerCase()} word</Caption>
      </StoryBook>
      <WritePad
        target={item.word}
        accent={accent}
        onWrong={onWrong}
        resetKey={`${index}-${item.word}`}
        onAccepted={() => {
          sfx?.("correct");
          fire();
          later(() => {
            if (index + 1 >= items.length) complete();
            else {
              sfx?.("pageturn", { volume: 0.5 });
              setIndex((n) => n + 1);
            }
          }, 300);
        }}
      />
    </Arena>
  );
};

// ── Find the Part ────────────────────────────────────────────────────────────

export const FindThePartGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const rounds = WRITING_L11.findRounds;
  const accent = activity.accent;
  const compact = useCompact();
  const [index, setIndex] = useState(0);
  const [solved, setSolved] = useState(null);
  const complete = useCompleteOnce(onComplete);
  const { shake, isShaking } = useShake();
  const [burst, fire] = useBurst();
  const later = useLater();
  const round = rounds[Math.min(index, rounds.length - 1)];
  const storyText = round.story.join(" ");
  useRoundCue(speak, `${storyText} Which sentence tells ${partTitle(WRITING_STORY_PARTS, round.part).toLowerCase()}?`, index);

  const choose = (i) => {
    if (solved != null) return;
    sfx?.("pop", { volume: 0.5 });
    if (i !== round.answerIndex) {
      onWrong?.();
      shake(i);
      speak?.("Listen once more. Which sentence tells that story part?", { interrupt: true, dedupe: false });
      return;
    }
    setSolved(i);
    sfx?.("correct");
    fire();
    speak?.(round.story[i], { interrupt: true, dedupe: false });
    later(() => {
      if (index + 1 >= rounds.length) complete();
      else {
        sfx?.("pageturn", { volume: 0.5 });
        setIndex((n) => n + 1);
        setSolved(null);
      }
    }, 1100);
  };

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={18} originY={26} reduceMotion={reduceMotion} />
      <StoryBook parts={WRITING_STORY_PARTS} activeId={round.part} accent={accent} compact={compact} reduceMotion={reduceMotion}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <SpeakerButton accent={accent} size={44} reduceMotion={reduceMotion} ariaLabel="Hear the tiny story" onClick={() => speak?.(storyText, { interrupt: true, dedupe: false })} />
          <Caption style={{ textAlign: "left", flex: 1 }}>Which sentence tells {partTitle(WRITING_STORY_PARTS, round.part).toLowerCase()}?</Caption>
          <RoundPill done={Math.min(index + 1, rounds.length)} total={rounds.length} accent={accent} />
        </div>
        {round.story.map((line, i) => (
          <StoryLine
            key={`${index}-${i}`}
            text={line}
            accent={accent}
            badge={i + 1}
            size={compact ? "md" : "lg"}
            tone={solved === i ? "correct" : isShaking(i) ? "wrong" : "idle"}
            shaking={isShaking(i)}
            pop={solved === i}
            reduceMotion={reduceMotion}
            onClick={() => choose(i)}
          />
        ))}
      </StoryBook>
    </Arena>
  );
};

// ── Sequential sentences (who / where / when / what / wants-and-but / then-and-so / feeling)

export const SequentialWriteGame = ({ activity, speak, sfx, onComplete, onWrong, profile, reduceMotion }) => {
  const spec = WRITING_L11.sequential[activity.id];
  const accent = activity.accent;
  const compact = useCompact();
  const [phase, setPhase] = useState(spec?.grown ? "short" : "write");
  const complete = useCompleteOnce(onComplete);
  const [burst, fire] = useBurst();
  const later = useLater();
  if (!spec) return null;
  const target = phase === "short" ? spec.short : spec.grown || spec.short;
  const grown = phase === "grown";
  const doneIds = Object.keys(writingLocalStore.lesson11Story(profile?.id).lines || {}).filter((id) => id !== spec.part);

  const save = (text) => {
    const state = writingLocalStore.lesson11Story(profile?.id);
    writingLocalStore.saveLesson11Story(profile?.id, { ...state, lines: { ...state.lines, [spec.part]: text } });
  };

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={18} originY={20} reduceMotion={reduceMotion} />
      <StoryBook parts={WRITING_STORY_PARTS} activeId={spec.part} doneIds={doneIds} accent={accent} compact={compact} reduceMotion={reduceMotion}>
        {spec.model && !grown && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, background: wash(accent, 0.1), borderRadius: 16, padding: "8px 10px" }}>
            <PictureTile name="pip" alt="Pip" cast accent={accent} size={0.9} as="div" tone="quiet" />
            <span style={{ flex: 1, fontSize: 16, fontWeight: 600, lineHeight: 1.3, color: LEARN.color.muted }}>{spec.model}</span>
            <SpeakerButton accent={accent} size={40} reduceMotion={reduceMotion} ariaLabel="Hear Pip's model" onClick={() => speak?.(spec.model, { interrupt: true, dedupe: false })} />
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <SpeakerButton accent={accent} size={44} reduceMotion={reduceMotion} ariaLabel="Hear the sentence" onClick={() => speak?.(target, { interrupt: true, dedupe: false })} />
          <div style={{ flex: 1, fontSize: compact ? 20 : 24, fontWeight: 700, lineHeight: 1.3 }}>
            <HighlightedText text={target} word={grown ? spec.growWord : ""} accent={accent} />
          </div>
          {spec.grown && <RoundPill done={grown ? 2 : 1} total={2} accent={accent} label={grown ? "Grow it" : "Write it"} />}
        </div>
      </StoryBook>
      <WritePad
        target={target}
        accent={accent}
        onWrong={onWrong}
        resetKey={`${activity.id}-${phase}`}
        onAccepted={() => {
          save(target);
          sfx?.("correct");
          fire();
          speak?.(target, { interrupt: true, dedupe: false });
          later(() => {
            if (phase === "short" && spec.grown) setPhase("grown");
            else complete();
          }, 400);
        }}
      />
    </Arena>
  );
};

// ── Order It ─────────────────────────────────────────────────────────────────

export const OrderItGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const moments = WRITING_L11.orderedMoments;
  const labels = WRITING_L11.orderLabels;
  const accent = activity.accent;
  const compact = useCompact();
  const [placed, setPlaced] = useState({});
  const [picked, setPicked] = useState(null);
  const [phase, setPhase] = useState("order");
  const [labelIndex, setLabelIndex] = useState(0);
  const complete = useCompleteOnce(onComplete);
  const { shake, isShaking } = useShake();
  const [burst, fire] = useBurst();
  const later = useLater();
  const tray = useMemo(() => seededShuffle(moments.map((text, i) => ({ id: i, text, asset: WRITING_L11.orderedMomentAssets[i] })), 7), [moments]);
  const placedIds = useMemo(() => new Set(Object.values(placed)), [placed]);

  const tryPlace = (item, slot) => {
    if (placed[slot] != null) return false;
    if (item.id !== slot) {
      onWrong?.();
      shake(`slot-${slot}`);
      return false;
    }
    const next = { ...placed, [slot]: item.id };
    setPlaced(next);
    setPicked(null);
    sfx?.("snap");
    speak?.(`${labels[slot]}, ${item.text}.`, { interrupt: true, dedupe: false });
    if (Object.keys(next).length >= moments.length) {
      sfx?.("correct");
      fire();
      later(() => setPhase("write"), 900);
    }
    return true;
  };

  const { dragProps, registerTarget, hoverId, GhostLayer } = useDragEngine({
    reduceMotion,
    onPickup: () => sfx?.("pickup", { volume: 0.5 }),
    onDrop: ({ payload, targetId, targetData }) => (targetId ? tryPlace(payload, targetData.index) : false),
  });

  const label = labels[Math.min(labelIndex, labels.length - 1)];

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={18} originY={22} reduceMotion={reduceMotion} />
      <StoryBook parts={WRITING_STORY_PARTS} activeId="order" accent={accent} compact={compact} reduceMotion={reduceMotion}>
        <div style={{ display: "flex", gap: 8, justifyContent: "space-between", alignItems: "flex-end" }}>
          {moments.map((_, slot) => {
            const item = tray.find((t) => t.id === placed[slot]);
            const active = phase === "write" && labelIndex === slot;
            return (
              <div key={slot} style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                <DropWordSlot
                  registerTarget={registerTarget}
                  id={`slot-${slot}`}
                  index={slot}
                  accent={accent}
                  hover={hoverId === `slot-${slot}`}
                  active={!!picked && !item}
                  shaking={isShaking(`slot-${slot}`)}
                  reduceMotion={reduceMotion}
                  ariaLabel={item ? `${labels[slot]}: ${item.text}` : `${labels[slot]} slot`}
                  minWidth="100%"
                  style={{ minHeight: `calc(var(--tile) * 1.3)`, width: "100%" }}
                  onClick={() => {
                    const it = tray.find((t) => t.id === picked);
                    if (it) tryPlace(it, slot);
                    else if (item) speak?.(item.text, { interrupt: true, dedupe: false });
                  }}
                >
                  {item && <PictureTile name={item.asset} alt={item.text} label={compact ? undefined : item.text} accent={accent} size={compact ? 1 : 1.2} tone="correct" as="div" pop reduceMotion={reduceMotion} />}
                </DropWordSlot>
                <WordCard text={label === labels[slot] && phase === "write" ? label : labels[slot]} as="div" size="sm" accent={accent} tone={active ? "selected" : item ? "hint" : "quiet"} style={{ minHeight: 40, padding: "4px 12px" }} />
              </div>
            );
          })}
        </div>
      </StoryBook>
      {phase === "order" ? (
        <Board title={activity.title} symbol={activity.symbol} accent={accent} caption="What happened first, then, and last?">
          <Rail gap={compact ? 12 : 16}>
            {tray.map((item) => {
              const used = placedIds.has(item.id);
              return (
                <PictureTile
                  key={item.id}
                  name={item.asset}
                  alt={item.text}
                  label={item.text}
                  accent={accent}
                  size={compact ? 1.3 : 1.6}
                  tone={used ? "blank" : picked === item.id ? "selected" : "idle"}
                  disabled={used}
                  reduceMotion={reduceMotion}
                  ariaLabel={used ? `${item.text}, placed` : `Moment: ${item.text}`}
                  drag={used ? undefined : dragProps(item, (p) => <PictureTile name={p.asset} alt={p.text} label={p.text} accent={accent} size={compact ? 1.3 : 1.6} tone="selected" as="div" />)}
                  onClick={() => {
                    sfx?.("pop", { volume: 0.5 });
                    speak?.(item.text, { interrupt: true, dedupe: false });
                    setPicked((p) => (p === item.id ? null : item.id));
                  }}
                  style={used ? { opacity: 0.35 } : undefined}
                />
              );
            })}
          </Rail>
        </Board>
      ) : (
        <WritePad
          target={label}
          accent={accent}
          onWrong={onWrong}
          resetKey={`label-${labelIndex}`}
          onAccepted={() => {
            sfx?.("correct");
            fire();
            later(() => {
              if (labelIndex + 1 >= labels.length) complete();
              else setLabelIndex((n) => n + 1);
            }, 300);
          }}
        />
      )}
      <GhostLayer />
    </Arena>
  );
};

// ── Endings Two Ways ─────────────────────────────────────────────────────────

export const EndingsTwoWaysGame = ({ activity, profile, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const endings = WRITING_L11.endings;
  const accent = activity.accent;
  const compact = useCompact();
  const [index, setIndex] = useState(0);
  const [choice, setChoice] = useState(null);
  const complete = useCompleteOnce(onComplete);
  const [burst, fire] = useBurst();
  const later = useLater();
  const choosing = index >= endings.length;
  const ending = endings[Math.min(index, endings.length - 1)];

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={18} originY={22} reduceMotion={reduceMotion} />
      <StoryBook parts={WRITING_STORY_PARTS} activeId="ending" accent={accent} compact={compact} reduceMotion={reduceMotion}>
        {!choosing ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <SpeakerButton accent={accent} size={44} reduceMotion={reduceMotion} ariaLabel="Hear the ending" onClick={() => speak?.(ending, { interrupt: true, dedupe: false })} />
            <div style={{ flex: 1, fontSize: compact ? 20 : 24, fontWeight: 700, lineHeight: 1.3 }}>{ending}</div>
            <RoundPill done={index + 1} total={endings.length} accent={accent} label="Ending" />
          </div>
        ) : (
          <>
            <Caption style={{ textAlign: "left" }}>Which ending fits your story best?</Caption>
            {endings.map((text, i) => (
              <StoryLine
                key={text}
                text={text}
                badge={i + 1}
                size={compact ? "md" : "lg"}
                accent={accent}
                tone={choice === text ? "correct" : "idle"}
                pop={choice === text}
                reduceMotion={reduceMotion}
                onClick={() => {
                  if (choice) return;
                  const state = writingLocalStore.lesson11Story(profile?.id);
                  writingLocalStore.saveLesson11Story(profile?.id, { ...state, chosenEnding: text, lines: { ...state.lines, ending: text } });
                  setChoice(text);
                  sfx?.("correct");
                  fire();
                  speak?.(text, { interrupt: true, dedupe: false });
                  later(complete, 500);
                }}
              />
            ))}
          </>
        )}
      </StoryBook>
      {!choosing && (
        <WritePad
          target={ending}
          accent={accent}
          onWrong={onWrong}
          resetKey={`ending-${index}`}
          onAccepted={() => {
            sfx?.("correct");
            fire();
            later(() => {
              sfx?.("pageturn", { volume: 0.5 });
              setIndex((n) => n + 1);
            }, 300);
          }}
        />
      )}
    </Arena>
  );
};

// ── Add to My Story ──────────────────────────────────────────────────────────

export const AddToMyStoryGame = ({ activity, profile, speak, sfx, onComplete, reduceMotion }) => {
  const accent = activity.accent;
  const compact = useCompact();
  const [state] = useState(() => writingLocalStore.lesson11Story(profile?.id));
  const lines = useMemo(() => lesson11Lines(state), [state]);
  const [glow, setGlow] = useState([]);
  const complete = useCompleteOnce(onComplete);
  const [burst, fire] = useBurst();

  const add = (partId) => {
    const line = lines.find((l) => l.part.id === partId);
    if (!line) return;
    sfx?.("pop", { volume: 0.5 });
    speak?.(line.text, { interrupt: true, dedupe: false });
    if (glow.includes(partId)) return;
    const next = [...glow, partId];
    setGlow(next);
    if (next.length >= lines.length) {
      writingLocalStore.saveLesson11Story(profile?.id, { ...state, savedToMyBook: true });
      sfx?.("sparkle");
      fire();
      speak?.(lines.map((l) => l.text).join(". "), { interrupt: true, dedupe: false });
      complete();
    }
  };

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={28} originY={30} reduceMotion={reduceMotion} />
      <StoryBook parts={WRITING_STORY_PARTS} doneIds={glow} accent={accent} compact={compact} reduceMotion={reduceMotion} tabs={{ onSelect: (part) => add(part.id) }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <SpeakerButton accent={accent} size={44} reduceMotion={reduceMotion} ariaLabel="Hear my whole story" onClick={() => speak?.(lines.map((l) => l.text).join(". "), { interrupt: true, dedupe: false })} />
          <Caption style={{ textAlign: "left", flex: 1 }}>Tap each line to add it to your story</Caption>
          <RoundPill done={glow.length} total={lines.length} accent={accent} />
        </div>
        {lines.map(({ part, text }, i) => (
          <StoryLine
            key={part.id}
            text={text}
            badge={`${i + 1} · ${part.title}`}
            accent={accent}
            tone={glow.includes(part.id) ? "correct" : "idle"}
            pop={glow[glow.length - 1] === part.id}
            reduceMotion={reduceMotion}
            onClick={() => add(part.id)}
          />
        ))}
      </StoryBook>
    </Arena>
  );
};

// ── Meet POW ─────────────────────────────────────────────────────────────────

export const MeetPOWGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const order = WRITING_L12.powOrder;
  const accent = activity.accent;
  const compact = useCompact();
  const [pass, setPass] = useState(0);
  const [placed, setPlaced] = useState({});
  const [picked, setPicked] = useState(null);
  const complete = useCompleteOnce(onComplete);
  const { shake, isShaking } = useShake();
  const [burst, fire] = useBurst();
  const later = useLater();
  const tray = useMemo(() => seededShuffle(order.map((text, i) => ({ id: i, text, letter: text[0] })), pass * 13 + 3), [order, pass]);
  const placedIds = useMemo(() => new Set(Object.values(placed)), [placed]);

  const tryPlace = (item, slot) => {
    if (placed[slot] != null) return false;
    if (item.id !== slot) {
      onWrong?.();
      shake(`slot-${slot}`);
      return false;
    }
    const next = { ...placed, [slot]: item.id };
    setPlaced(next);
    setPicked(null);
    sfx?.("snap");
    speak?.(item.text, { interrupt: true, dedupe: false });
    if (Object.keys(next).length >= order.length) {
      sfx?.("correct");
      fire();
      later(() => {
        if (pass + 1 >= 2) complete();
        else {
          sfx?.("pageturn", { volume: 0.5 });
          setPass((n) => n + 1);
          setPlaced({});
        }
      }, 1000);
    }
    return true;
  };

  const { dragProps, registerTarget, hoverId, GhostLayer } = useDragEngine({
    reduceMotion,
    onPickup: () => sfx?.("pickup", { volume: 0.5 }),
    onDrop: ({ payload, targetId, targetData }) => (targetId ? tryPlace(payload, targetData.index) : false),
  });

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={18} originY={22} reduceMotion={reduceMotion} />
      <Panel style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Rail gap={6} style={{ justifyContent: "flex-start" }}>
            {["P", "O", "W"].map((l, i) => (
              <span key={l} style={{ width: 38, height: 38, borderRadius: 11, display: "grid", placeItems: "center", fontWeight: 700, fontSize: 20, background: placed[i] != null ? accent : wash(accent, 0.14), color: placed[i] != null ? "#fff" : darken(accent, 0.15), boxShadow: placed[i] != null ? LEARN.shadow.sticker(darken(accent)) : "none", transition: reduceMotion ? "none" : "background 160ms" }}>
                {l}
              </span>
            ))}
          </Rail>
          <RoundPill done={pass + 1} total={2} accent={accent} label="Round" />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {order.map((_, slot) => {
            const item = tray.find((t) => t.id === placed[slot]);
            return (
              <DropWordSlot
                key={`${pass}-${slot}`}
                registerTarget={registerTarget}
                id={`slot-${slot}`}
                index={slot}
                accent={accent}
                hover={hoverId === `slot-${slot}`}
                active={!!picked && !item}
                shaking={isShaking(`slot-${slot}`)}
                reduceMotion={reduceMotion}
                label={`Step ${slot + 1}`}
                ariaLabel={item ? `Step ${slot + 1}: ${item.text}` : `Step ${slot + 1}`}
                minWidth="100%"
                style={{ justifyContent: "flex-start", paddingLeft: item ? 0 : 14 }}
                onClick={() => {
                  const it = tray.find((t) => t.id === picked);
                  if (it) tryPlace(it, slot);
                  else if (item) speak?.(item.text, { interrupt: true, dedupe: false });
                }}
              >
                {item && <StoryLine text={item.text} badge={slot + 1} accent={accent} tone="correct" as="div" pop reduceMotion={reduceMotion} />}
              </DropWordSlot>
            );
          })}
        </div>
      </Panel>
      <Board title={activity.title} symbol={activity.symbol} accent={accent} caption="Pick, Organize, Write. Put the steps in order." bodyStyle={{ alignItems: "stretch", gap: 10 }}>
        {tray.map((item) => {
          const used = placedIds.has(item.id);
          return (
            <StoryLine
              key={`${pass}-${item.id}`}
              text={item.text}
              badge={item.letter}
              size={compact ? "md" : "lg"}
              accent={accent}
              tone={used ? "idle" : picked === item.id ? "selected" : "idle"}
              reduceMotion={reduceMotion}
              ariaLabel={used ? `${item.text}, placed` : `Step: ${item.text}`}
              drag={used ? undefined : dragProps(item, (p) => <StoryLine text={p.text} badge={p.letter} size={compact ? "md" : "lg"} accent={accent} tone="selected" as="div" style={{ width: "auto" }} />)}
              onClick={
                used
                  ? undefined
                  : () => {
                      sfx?.("pop", { volume: 0.5 });
                      speak?.(item.text, { interrupt: true, dedupe: false });
                      setPicked((p) => (p === item.id ? null : item.id));
                    }
              }
              style={used ? { opacity: 0.3 } : undefined}
            />
          );
        })}
      </Board>
      <GhostLayer />
    </Arena>
  );
};

// ── Part Detective ───────────────────────────────────────────────────────────

export const PartDetectiveGame = ({ activity, speak, sfx, onComplete, reduceMotion }) => {
  const story = WRITING_L12.detectiveStory;
  const accent = activity.accent;
  const compact = useCompact();
  const [found, setFound] = useState([]);
  const complete = useCompleteOnce(onComplete);
  const [burst, fire] = useBurst();
  const all = story.map((s) => s.text).join(" ");

  const find = (item) => {
    sfx?.("pop", { volume: 0.5 });
    speak?.(`${item.text} That tells ${partTitle(WRITING_L12_PARTS, item.part).toLowerCase()}.`, { interrupt: true, dedupe: false });
    if (found.includes(item.part)) return;
    const next = [...found, item.part];
    setFound(next);
    if (next.length >= WRITING_L12_PARTS.length) {
      sfx?.("sparkle");
      fire();
      complete();
    } else sfx?.("snap");
  };

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={26} originY={30} reduceMotion={reduceMotion} />
      <StoryBook parts={WRITING_L12_PARTS} doneIds={found} accent={accent} compact={compact} reduceMotion={reduceMotion} tabs={{ onSelect: (part) => { const item = story.find((s) => s.part === part.id); if (item) find(item); } }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <SpeakerButton accent={accent} size={44} reduceMotion={reduceMotion} ariaLabel="Hear the story" onClick={() => speak?.(all, { interrupt: true, dedupe: false })} />
          <Caption style={{ textAlign: "left", flex: 1 }}>Tap each sentence to find its story part</Caption>
          <RoundPill done={found.length} total={WRITING_L12_PARTS.length} accent={accent} label="Found" />
        </div>
        {story.map((item, i) => {
          const done = found.includes(item.part);
          return (
            <StoryLine
              key={item.part}
              text={item.text}
              badge={done ? partTitle(WRITING_L12_PARTS, item.part) : i + 1}
              accent={accent}
              tone={done ? "correct" : "idle"}
              pop={found[found.length - 1] === item.part}
              reduceMotion={reduceMotion}
              onClick={() => find(item)}
            />
          );
        })}
      </StoryBook>
    </Arena>
  );
};

// ── Parts Sort ───────────────────────────────────────────────────────────────

export const PartsSortGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const story = WRITING_L12.sortStory;
  const accent = activity.accent;
  const compact = useCompact();
  const [index, setIndex] = useState(0);
  const [solved, setSolved] = useState(false);
  const complete = useCompleteOnce(onComplete);
  const { shake, shakeId } = useShake();
  const [burst, fire] = useBurst();
  const later = useLater();
  const item = story[Math.min(index, story.length - 1)];
  const doneIds = story.slice(0, index).map((s) => s.part);
  useRoundCue(speak, item.text, index);

  const trySort = (partId) => {
    if (solved) return false;
    if (partId !== item.part) {
      onWrong?.();
      shake(partId);
      return false;
    }
    setSolved(true);
    sfx?.("snap");
    sfx?.("correct");
    fire();
    speak?.(`${partTitle(WRITING_L12_PARTS, partId)}.`, { interrupt: true, dedupe: false });
    later(() => {
      if (index + 1 >= story.length) complete();
      else {
        sfx?.("pageturn", { volume: 0.5 });
        setIndex((n) => n + 1);
        setSolved(false);
      }
    }, 900);
    return true;
  };

  const { dragProps, registerTarget, hoverId, GhostLayer } = useDragEngine({
    reduceMotion,
    onPickup: () => sfx?.("pickup", { volume: 0.5 }),
    onDrop: ({ targetId, targetData }) => (targetId ? trySort(targetData.part) : false),
  });

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={18} originY={16} reduceMotion={reduceMotion} />
      <StoryBook
        parts={WRITING_L12_PARTS}
        activeId={solved ? item.part : undefined}
        doneIds={doneIds}
        accent={accent}
        compact={compact}
        reduceMotion={reduceMotion}
        minHeight="clamp(150px, calc(var(--arena-h, 600px) * 0.28), 240px)"
        tabs={{
          hoverId,
          shakingId: shakeId,
          tabRef: (part) => (el) => registerTarget(`tab-${part.id}`, el, { part: part.id }),
          onSelect: (part) => trySort(part.id),
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <SpeakerButton accent={accent} size={44} reduceMotion={reduceMotion} ariaLabel="Hear the sentence" onClick={() => speak?.(item.text, { interrupt: true, dedupe: false })} />
          <Caption style={{ textAlign: "left", flex: 1 }}>Drag the sentence to its part, or tap the part</Caption>
          <RoundPill done={Math.min(index + 1, story.length)} total={story.length} accent={accent} />
        </div>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "calc(var(--tile, 72px) * 0.25) 0 calc(var(--tile, 72px) * 0.15)" }}>
          <StoryLine
            key={index}
            text={item.text}
            badge={index + 1}
            size={compact ? "md" : "lg"}
            accent={accent}
            tone={solved ? "correct" : "selected"}
            pop={solved}
            reduceMotion={reduceMotion}
            ariaLabel={`Sentence: ${item.text}`}
            drag={solved ? undefined : dragProps({ part: item.part }, () => <StoryLine text={item.text} badge={index + 1} size={compact ? "md" : "lg"} accent={accent} tone="selected" as="div" />)}
            onClick={() => speak?.(item.text, { interrupt: true, dedupe: false })}
          />
        </div>
      </StoryBook>
      <GhostLayer />
    </Arena>
  );
};

// ── Panda Plans Out Loud ─────────────────────────────────────────────────────

const THINK_PARTS = WRITING_L12.thinkAloud.map((s, i) => ({ id: `step-${i}`, title: s.title }));

export const PandaPlansGame = ({ activity, speak, sfx, onComplete, reduceMotion }) => {
  const steps = WRITING_L12.thinkAloud;
  const accent = activity.accent;
  const compact = useCompact();
  const [index, setIndex] = useState(0);
  const [heard, setHeard] = useState([]);
  const complete = useCompleteOnce(onComplete);
  const step = steps[Math.min(index, steps.length - 1)];
  const heardThis = heard.includes(index);
  const last = index >= steps.length - 1;

  const hear = () => {
    sfx?.("pop", { volume: 0.4 });
    const p = speak?.(step.text, { interrupt: true, dedupe: false });
    const mark = () => setHeard((h) => (h.includes(index) ? h : [...h, index]));
    if (p && typeof p.then === "function") p.then(mark);
    else mark();
  };

  const turn = () => {
    if (!heardThis) return;
    if (last) {
      sfx?.("sparkle");
      complete();
      return;
    }
    sfx?.("pageturn", { volume: 0.5 });
    setIndex((n) => n + 1);
  };

  return (
    <Arena>
      <StoryBook
        parts={THINK_PARTS}
        activeId={`step-${index}`}
        doneIds={heard.map((i) => `step-${i}`)}
        accent={accent}
        compact={compact}
        reduceMotion={reduceMotion}
        art="pip"
        cast
        artAlt="Pip"
        tabs={{ onSelect: (part) => { const i = Number(part.id.replace("step-", "")); if (heard.includes(i) || i === index) setIndex(i); } }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: darken(accent, 0.15), background: wash(accent, 0.14), borderRadius: 999, padding: "5px 12px" }}>{step.title}</span>
          <div style={{ flex: 1 }} />
          <RoundPill done={index + 1} total={steps.length} accent={accent} label="Page" />
        </div>
        <div style={{ flex: 1, fontSize: compact ? 21 : 26, fontWeight: 700, lineHeight: 1.35, padding: "6px 0" }}>{step.text}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <ActionButton accent={accent} reduceMotion={reduceMotion} icon={<SpeakerIcon size={18} />} onClick={hear}>
            {heardThis ? "Hear Pip again" : "Hear Pip"}
          </ActionButton>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="wt-focus"
            aria-label={last ? "Finish listening" : "Turn the page"}
            disabled={!heardThis}
            onClick={turn}
            style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              border: "none",
              background: heardThis ? accent : "rgba(11,26,51,0.14)",
              boxShadow: heardThis ? LEARN.shadow.sticker(darken(accent)) : "none",
              display: "grid",
              placeItems: "center",
              cursor: heardThis ? "pointer" : "not-allowed",
              transition: reduceMotion ? "none" : "background 160ms",
            }}
          >
            {last ? <CheckIcon size={24} /> : <ArrowIcon size={24} />}
          </button>
        </div>
      </StoryBook>
    </Arena>
  );
};

// ── My Organizer ─────────────────────────────────────────────────────────────

export const MyOrganizerGame = ({ activity, profile, speak, sfx, onComplete, reduceMotion }) => {
  const parts = WRITING_L12_PARTS;
  const accent = activity.accent;
  const compact = useCompact();
  const [state, setState] = useState(() => writingLocalStore.lesson12Story(profile?.id));
  const [active, setActive] = useState(() => parts.find((p) => !state.organizer?.[p.id])?.id || parts[0].id);
  const complete = useCompleteOnce(onComplete);
  const [burst, fire] = useBurst();
  const later = useLater();
  const organizer = state.organizer || {};
  const filledIds = parts.filter((p) => organizer[p.id]).map((p) => p.id);
  const part = parts.find((p) => p.id === active) || parts[0];
  const goal = WRITING_L12.minimumOrganizerParts;

  const pick = (value) => {
    sfx?.("pop", { volume: 0.5 });
    speak?.(value, { interrupt: true, dedupe: false });
    const nextOrganizer = { ...organizer, [part.id]: value };
    const next = { ...state, organizer: nextOrganizer };
    setState(next);
    writingLocalStore.saveLesson12Story(profile?.id, next);
    const filled = parts.filter((p) => nextOrganizer[p.id]).length;
    if (filled >= goal) {
      fire();
      complete();
    }
    later(() => {
      const following = parts.slice(parts.indexOf(part) + 1).find((p) => !nextOrganizer[p.id]) || parts.find((p) => !nextOrganizer[p.id]);
      if (following) setActive(following.id);
    }, 550);
  };

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={22} originY={26} reduceMotion={reduceMotion} />
      <StoryBook parts={parts} activeId={part.id} doneIds={filledIds} accent={accent} compact={compact} reduceMotion={reduceMotion} tabs={{ onSelect: (p) => { sfx?.("pageturn", { volume: 0.4 }); setActive(p.id); } }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: compact ? 20 : 24, fontWeight: 700 }}>{part.title}</span>
          <div style={{ flex: 1 }} />
          <RoundPill done={filledIds.length} total={parts.length} accent={accent} label="Boxes" />
        </div>
        <Caption style={{ textAlign: "left" }}>{filledIds.length >= goal ? "Five boxes are full. Add more if you like." : `Notes, not sentences yet. Fill ${goal} boxes.`}</Caption>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {(WRITING_L12.organizerChoices[part.id] || []).map((choice) => (
            <StoryLine
              key={choice}
              text={choice}
              accent={accent}
              tone={organizer[part.id] === choice ? "correct" : "idle"}
              pop={organizer[part.id] === choice}
              reduceMotion={reduceMotion}
              onClick={() => pick(choice)}
            />
          ))}
        </div>
      </StoryBook>
    </Arena>
  );
};

// ── Set My Goal ──────────────────────────────────────────────────────────────

export const SetMyGoalGame = ({ activity, profile, speak, sfx, onComplete, reduceMotion }) => {
  const accent = activity.accent;
  const compact = useCompact();
  const [goal, setGoal] = useState(null);
  const complete = useCompleteOnce(onComplete);
  const [burst, fire] = useBurst();
  const later = useLater();

  const choose = (n) => {
    if (goal) return;
    const state = writingLocalStore.lesson12Story(profile?.id);
    writingLocalStore.saveLesson12Story(profile?.id, { ...state, goal: n });
    setGoal(n);
    sfx?.("correct");
    fire();
    speak?.(n === 7 ? "Seven parts. Every part of the story." : "Five parts. A friendly first try.", { interrupt: true, dedupe: false });
    later(complete, 500);
  };

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={22} originY={30} reduceMotion={reduceMotion} />
      <Board title={activity.title} symbol={activity.symbol} accent={accent} caption="How many story parts will you write?" bodyStyle={{ alignItems: "stretch" }}>
        <div style={{ display: "flex", flexDirection: compact ? "column" : "row", alignItems: "stretch", gap: 16, width: "100%" }}>
          {WRITING_L12.goalChoices.map((n) => {
            const chosen = goal === n;
            return (
              <button
                key={n}
                type="button"
                className="wt-focus"
                aria-label={`${n} parts`}
                onClick={() => choose(n)}
                style={{
                  flex: 1,
                  minHeight: compact ? 140 : 200,
                  borderRadius: 22,
                  border: `2px solid ${chosen ? LEARN.color.mintDeep : goal ? LEARN.color.line : wash(accent, 0.4)}`,
                  background: chosen ? LEARN.color.mint : "#fff",
                  color: chosen ? "#fff" : LEARN.color.ink,
                  boxShadow: chosen ? LEARN.shadow.sticker(LEARN.color.mintDeep) : `0 4px 0 ${darken(accent)}, 0 10px 20px rgba(11,26,51,0.08)`,
                  WebkitTapHighlightColor: "transparent",
                  fontFamily: LEARN.font,
                  cursor: goal ? "default" : "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  opacity: goal && !chosen ? 0.5 : 1,
                  transition: reduceMotion ? "none" : "background 160ms, opacity 200ms",
                }}
              >
                <span style={{ fontSize: compact ? 48 : 64, fontWeight: 700, lineHeight: 1 }}>{n}</span>
                <span style={{ fontSize: 18, fontWeight: 700 }}>story parts</span>
                <div style={{ display: "flex", gap: 4 }}>
                  {WRITING_L12_PARTS.map((p, i) => (
                    <span key={p.id} aria-hidden="true" style={{ width: compact ? 18 : 24, height: compact ? 24 : 32, borderRadius: 6, background: i < n ? (chosen ? "rgba(255,255,255,0.7)" : accent) : "rgba(11,26,51,0.1)" }} />
                  ))}
                </div>
                <span style={{ fontSize: 14, fontWeight: 600, opacity: 0.8 }}>{n === 7 ? "Use every part" : "A friendly first try"}</span>
              </button>
            );
          })}
        </div>
      </Board>
    </Arena>
  );
};

// ── Draft With the Meter ─────────────────────────────────────────────────────

export const DraftWithMeterGame = ({ activity, profile, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const parts = WRITING_L12_PARTS;
  const accent = activity.accent;
  const compact = useCompact();
  const [state, setState] = useState(() => writingLocalStore.lesson12Story(profile?.id));
  const complete = useCompleteOnce(onComplete);
  const [burst, fire] = useBurst();
  const count = state.draftedSentenceCount || 0;
  const marked = state.draftedParts || [];
  const goal = state.goal || WRITING_L12.minimumOrganizerParts;
  const need = WRITING_L12.minimumDraftSentences;
  const phrases = useMemo(() => {
    const list = parts.filter((p) => state.organizer?.[p.id]).map((p) => ({ part: p.id, text: state.organizer[p.id] }));
    return list.length ? list : [{ part: "who", text: "Pip" }];
  }, [parts, state.organizer]);
  const current = phrases[count % phrases.length];

  const save = (next) => {
    setState(next);
    writingLocalStore.saveLesson12Story(profile?.id, next);
    if ((next.draftedSentenceCount || 0) >= need && (next.draftedParts || []).length >= goal) {
      sfx?.("sparkle");
      fire();
      complete();
    }
  };

  const mark = (partId) => {
    sfx?.("pop", { volume: 0.5 });
    if (marked.includes(partId)) return;
    save({ ...state, draftedParts: [...marked, partId] });
  };

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={24} originY={24} reduceMotion={reduceMotion} />
      <StoryBook parts={parts} doneIds={marked} accent={accent} compact={compact} reduceMotion={reduceMotion} tabs={{ onSelect: (p) => mark(p.id) }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <ProgressRing progress={Math.min(1, count / need)} accent={accent} size={compact ? 60 : 76} label={count} sub={`of ${need}`} />
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
            <Caption style={{ textAlign: "left" }}>Write from your notes, then tap a part it lands in</Caption>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: compact ? 18 : 22, fontWeight: 700, lineHeight: 1.25 }}>{current.text}</span>
              <SpeakerButton accent={accent} size={36} reduceMotion={reduceMotion} ariaLabel="Hear it" onClick={() => speak?.(current.text, { interrupt: true, dedupe: false })} />
            </div>
          </div>
          <RoundPill done={marked.length} total={goal} accent={accent} label="Parts" />
        </div>
      </StoryBook>
      <WritePad
        target={current.text}
        accent={accent}
        onWrong={onWrong}
        resetKey={`draft-${count}`}
        onAccepted={() => {
          sfx?.("correct");
          const nextParts = marked.includes(current.part) ? marked : [...marked, current.part];
          save({ ...state, draftedSentenceCount: count + 1, draftedParts: nextParts });
        }}
      />
    </Arena>
  );
};

// ── Three Questions ──────────────────────────────────────────────────────────

export const ThreeQuestionsGame = ({ activity, profile, speak, sfx, onComplete, reduceMotion }) => {
  const questions = WRITING_L12.revisionQuestions;
  const accent = activity.accent;
  const [checked, setChecked] = useState([]);
  const complete = useCompleteOnce(onComplete);
  const [burst, fire] = useBurst();
  const allChecked = checked.length >= questions.length;

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={22} originY={30} reduceMotion={reduceMotion} />
      <Board title={activity.title} symbol={activity.symbol} accent={accent} bodyStyle={{ alignItems: "stretch", gap: 10 }}>
        {questions.map((q) => (
          <CheckCard
            key={q}
            text={q}
            checked={checked.includes(q)}
            accent={accent}
            reduceMotion={reduceMotion}
            onClick={() => {
              sfx?.("pop", { volume: 0.5 });
              speak?.(q, { interrupt: true, dedupe: false });
              setChecked((c) => (c.includes(q) ? c : [...c, q]));
            }}
          />
        ))}
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 6 }}>
          <ActionButton
            accent={accent}
            reduceMotion={reduceMotion}
            disabled={!allChecked}
            icon={<CheckIcon size={18} />}
            onClick={() => {
              const state = writingLocalStore.lesson12Story(profile?.id);
              writingLocalStore.saveLesson12Story(profile?.id, { ...state, revisionChangeMade: true });
              sfx?.("correct");
              fire();
              complete();
            }}
          >
            I made one change
          </ActionButton>
        </div>
      </Board>
    </Arena>
  );
};

// ── Grow One Sentence ────────────────────────────────────────────────────────

export const GrowOneSentenceGame = ({ activity, profile, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const accent = activity.accent;
  const [joiner, setJoiner] = useState(null);
  const [picked, setPicked] = useState(null);
  const complete = useCompleteOnce(onComplete);
  const [burst, fire] = useBurst();
  const sentence = joiner ? `Pip planted a seed ${joiner} it grew` : "";

  const choose = (j) => {
    sfx?.("snap");
    setJoiner(j);
    setPicked(null);
    speak?.(`Pip planted a seed ${j} it grew.`, { interrupt: true, dedupe: false });
  };

  const { dragProps, registerTarget, hoverId, GhostLayer } = useDragEngine({
    reduceMotion,
    onPickup: () => sfx?.("pickup", { volume: 0.5 }),
    onDrop: ({ payload, targetId }) => {
      if (!targetId) return false;
      choose(payload.joiner);
      return true;
    },
  });

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={18} originY={20} reduceMotion={reduceMotion} />
      <Panel style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <Rail gap={6}>
          {["Pip", "planted", "a", "seed"].map((w) => (
            <WordCard key={w} text={w} as="div" size="sm" accent={accent} />
          ))}
          <DropWordSlot
            registerTarget={registerTarget}
            id="joiner"
            index={0}
            accent={accent}
            hover={hoverId === "joiner"}
            active={!!picked}
            reduceMotion={reduceMotion}
            label="joining word"
            ariaLabel={joiner || "joining word slot"}
            onClick={() => picked && choose(picked)}
          >
            {joiner && <WordCard text={joiner} as="div" size="sm" accent={accent} tone="selected" pop reduceMotion={reduceMotion} />}
          </DropWordSlot>
          {["it", "grew"].map((w) => (
            <WordCard key={w} text={w} as="div" size="sm" accent={accent} />
          ))}
          {joiner && <SpeakerButton accent={accent} size={40} reduceMotion={reduceMotion} ariaLabel="Hear the sentence" onClick={() => speak?.(`${sentence}.`, { interrupt: true, dedupe: false })} />}
        </Rail>
      </Panel>
      {!joiner ? (
        <Board title={activity.title} symbol={activity.symbol} accent={accent} caption="Choose a joining word to grow the sentence">
          <Rail gap={16}>
            {WRITING_L12.growChoices.map((j) => (
              <WordCard
                key={j}
                text={j}
                size="lg"
                accent={accent}
                tone={picked === j ? "selected" : "idle"}
                reduceMotion={reduceMotion}
                ariaLabel={`Choose ${j}`}
                drag={dragProps({ joiner: j }, (p) => <WordCard text={p.joiner} size="lg" as="div" accent={accent} tone="selected" />)}
                onClick={() => {
                  sfx?.("pop", { volume: 0.5 });
                  speak?.(j, { interrupt: true, dedupe: false });
                  setPicked((p) => (p === j ? null : j));
                }}
              />
            ))}
          </Rail>
        </Board>
      ) : (
        <WritePad
          target={sentence}
          accent={accent}
          onWrong={onWrong}
          resetKey={sentence}
          onAccepted={() => {
            const state = writingLocalStore.lesson12Story(profile?.id);
            writingLocalStore.saveLesson12Story(profile?.id, { ...state, grownSentence: true });
            sfx?.("correct");
            fire();
            complete();
          }}
        />
      )}
      <GhostLayer />
    </Arena>
  );
};

// ── Into My Book ─────────────────────────────────────────────────────────────

function organizerPages(state) {
  return WRITING_L12_PARTS.filter((p) => state.organizer?.[p.id]).map((p) => ({ part: p, text: state.organizer[p.id] }));
}

export const IntoMyBookGame = ({ activity, profile, speak, sfx, onComplete, reduceMotion }) => {
  const accent = activity.accent;
  const compact = useCompact();
  const [state] = useState(() => writingLocalStore.lesson12Story(profile?.id));
  const pages = useMemo(() => organizerPages(state), [state]);
  const [saved, setSaved] = useState(false);
  const complete = useCompleteOnce(onComplete);
  const [burst, fire] = useBurst();
  const storyText = pages.map((p) => p.text).join(". ");

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={28} originY={30} reduceMotion={reduceMotion} />
      <StoryBook parts={WRITING_L12_PARTS} doneIds={pages.map((p) => p.part.id)} accent={accent} compact={compact} reduceMotion={reduceMotion} minHeight="clamp(150px, calc(var(--arena-h, 600px) * 0.26), 240px)">
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", justifyContent: compact ? "center" : "flex-start" }}>
          <ActionButton accent={accent} size={compact ? "md" : "lg"} reduceMotion={reduceMotion} icon={<SpeakerIcon size={20} />} onClick={() => speak?.(storyText || "Your organizer is empty. You can still save your book.", { interrupt: true, dedupe: false })}>
            Hear my story
          </ActionButton>
          {!compact && <div style={{ flex: 1 }} />}
          <ActionButton
            accent={saved ? LEARN.color.mint : accent}
            size={compact ? "md" : "lg"}
            reduceMotion={reduceMotion}
            disabled={saved}
            icon={saved ? <CheckIcon size={20} /> : undefined}
            onClick={() => {
              writingLocalStore.saveLesson12Story(profile?.id, { ...state, savedToMyBook: true });
              setSaved(true);
              sfx?.("pageturn", { volume: 0.6 });
              sfx?.("sparkle");
              fire();
              complete();
            }}
          >
            {saved ? "Saved to My Book" : "Save to My Book"}
          </ActionButton>
        </div>
        {pages.length ? (
          pages.map(({ part, text }, i) => (
            <StoryLine key={part.id} text={text} badge={`${i + 1} · ${part.title}`} accent={accent} tone={saved ? "correct" : "idle"} onClick={() => speak?.(text, { interrupt: true, dedupe: false })} ariaLabel={`Hear ${part.title}`} reduceMotion={reduceMotion} />
          ))
        ) : (
          <div style={{ flex: 1, display: "grid", placeItems: "center", padding: "calc(var(--tile, 72px) * 0.3) 0" }}>
            <Caption>Your organizer is empty on this device. You can still save your book.</Caption>
          </div>
        )}
      </StoryBook>
    </Arena>
  );
};

// ── Reader Reply ─────────────────────────────────────────────────────────────

export const ReaderReplyGame = ({ activity, profile, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const accent = activity.accent;
  const compact = useCompact();
  const [state] = useState(() => writingLocalStore.lesson12Story(profile?.id));
  const pages = useMemo(() => organizerPages(state), [state]);
  const complete = useCompleteOnce(onComplete);
  const [burst, fire] = useBurst();
  const target = "The ending surprised me";

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={22} originY={24} reduceMotion={reduceMotion} />
      <Panel style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: "42%", overflow: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <SpeakerButton accent={accent} size={44} reduceMotion={reduceMotion} ariaLabel="Hear the question" onClick={() => speak?.(WRITING_L12.readerQuestion, { interrupt: true, dedupe: false })} />
          <div style={{ flex: 1, fontSize: compact ? 20 : 24, fontWeight: 700, lineHeight: 1.3 }}>{WRITING_L12.readerQuestion}</div>
        </div>
        {pages.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <Caption style={{ textAlign: "left" }}>For your reader</Caption>
            {pages.map(({ part, text }) => (
              <StoryLine key={part.id} text={text} badge={part.title} accent={accent} as="div" style={{ fontSize: 16, padding: "8px 12px" }} />
            ))}
          </div>
        )}
      </Panel>
      <WritePad
        target={target}
        accent={accent}
        onWrong={onWrong}
        onAccepted={() => {
          writingLocalStore.saveLesson12Story(profile?.id, { ...state, replyWritten: true });
          sfx?.("correct");
          fire();
          complete();
        }}
      />
    </Arena>
  );
};
